/**
 * Admin Promotions Page
 * Manage promotion codes and discounts
 */

import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';

interface Promotion {
  id: number;
  code: string;
  name: string;
  description: string | null;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  appliesTo: 'setup' | 'recurring' | 'both';
  minOrderValue: number;
  maxDiscountAmount: number | null;
  appliesToAllProducts: number;
  maxUses: number | null;
  maxUsesPerClient: number;
  requireExistingClient: number;
  newClientsOnly: number;
  recurringDiscount: number;
  recurringMonths: number | null;
  startsAt: string | null;
  expiresAt: string | null;
  isActive: number;
  totalRedemptions: number;
  totalDiscountGiven: number;
  createdAt: string;
  updatedAt: string;
}

export const PromotionsPage: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    isActive: '',
    discountType: '',
    appliesTo: '',
    search: '',
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedPromotionId, setSelectedPromotionId] = useState<number | null>(null);

  useEffect(() => {
    loadPromotions();
  }, [filters]);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (filters.isActive) params.append('isActive', filters.isActive);
      if (filters.discountType) params.append('discountType', filters.discountType);
      if (filters.appliesTo) params.append('appliesTo', filters.appliesTo);
      if (filters.search) params.append('search', filters.search);

      const response = await adminApi.get(`/promotions?${params.toString()}`);
      setPromotions(response.data.promotions);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load promotions');
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (promotionId: number) => {
    try {
      await adminApi.post(`/promotions/${promotionId}/toggle`);
      loadPromotions();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle promotion');
      console.error('Error toggling promotion:', err);
    }
  };

  const handleDelete = async (promotionId: number, code: string) => {
    if (!confirm(`Are you sure you want to delete promotion "${code}"?`)) {
      return;
    }

    try {
      await adminApi.delete(`/promotions/${promotionId}`);
      loadPromotions();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete promotion');
      console.error('Error deleting promotion:', err);
    }
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingPromotion(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingPromotion(null);
    loadPromotions();
  };

  const handleViewUsage = (promotionId: number) => {
    setSelectedPromotionId(promotionId);
    setShowUsageModal(true);
  };

  const formatDiscount = (type: string, value: number) => {
    if (type === 'percentage') {
      return `${value}%`;
    }
    return `$${value.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Promotions</h1>
            <p className="text-slate-400 mt-1">Manage discount codes and promotions</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            + Create Promotion
          </button>
        </div>

        {/* Filters */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Discount Type
              </label>
              <select
                value={filters.discountType}
                onChange={(e) => setFilters({ ...filters, discountType: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Applies To</label>
              <select
                value={filters.appliesTo}
                onChange={(e) => setFilters({ ...filters, appliesTo: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="setup">Setup Fee</option>
                <option value="recurring">Recurring Price</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Search</label>
              <input
                type="text"
                placeholder="Code or name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : promotions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400">No promotions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Applies To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Uses
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {promotions.map((promotion) => (
                    <tr key={promotion.id} className="hover:bg-slate-700/50/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-bold text-orange-400">
                          {promotion.code}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{promotion.name}</div>
                        {promotion.description && (
                          <div className="text-xs text-slate-400 mt-1">
                            {promotion.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {formatDiscount(promotion.discountType, promotion.discountValue)}
                        </div>
                        <div className="text-xs text-slate-400 capitalize">
                          {promotion.discountType}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs rounded-full bg-slate-700/50 text-slate-300 capitalize">
                          {promotion.appliesTo}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {promotion.totalRedemptions}
                          {promotion.maxUses && ` / ${promotion.maxUses}`}
                        </div>
                        <div className="text-xs text-slate-400">
                          ${promotion.totalDiscountGiven.toFixed(2)} given
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-300">
                          {formatDate(promotion.expiresAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            promotion.isActive
                              ? 'bg-green-900/30 text-green-400'
                              : 'bg-red-900/30 text-red-400'
                          }`}
                        >
                          {promotion.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(promotion)}
                            className="text-orange-400 hover:text-orange-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleActive(promotion.id)}
                            className="text-yellow-400 hover:text-yellow-300"
                          >
                            {promotion.isActive ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => handleViewUsage(promotion.id)}
                            className="text-purple-400 hover:text-purple-300"
                          >
                            Usage
                          </button>
                          <button
                            onClick={() => handleDelete(promotion.id, promotion.code)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <PromotionFormModal
          promotion={editingPromotion}
          onClose={handleModalClose}
        />
      )}

      {showUsageModal && selectedPromotionId && (
        <UsageStatisticsModal
          promotionId={selectedPromotionId}
          onClose={() => setShowUsageModal(false)}
        />
      )}
    </div>
  );
};

// Import modal components (to be created next)
import { PromotionFormModal } from '../../components/admin/PromotionFormModal';
import { UsageStatisticsModal } from '../../components/admin/UsageStatisticsModal';

export default PromotionsPage;
